import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ArrowLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { navigateToPublicProfile } from '../utils/profileUtils';
import { generateInitialsAvatar } from '../utils/avatarUtils';
import { useCurrency } from '../hooks/useCurrency';

interface Donor {
  userId?: string;
  username?: string;
  amount: number;
  date: Date;
  isAnonymous: boolean;
}

interface RequestInfo {
  title: string;
  creatorName: string;
  creatorProfilePicture?: string;
  creatorCity: string;
  creatorCountry: string;
  creatorId: string;
}

export function DonorsList() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [donors, setDonors] = useState<Donor[]>([]);
  const [requestInfo, setRequestInfo] = useState<RequestInfo | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const currency = useCurrency();

  useEffect(() => {
    const fetchDonors = async () => {
      if (!id) return;
      
      const requestDoc = await getDoc(doc(db, '2dr_requests', id));
      if (requestDoc.exists()) {
        const data = requestDoc.data();
        const donations = data.donations || [];

        // Sauvegarder les informations de la demande
        setRequestInfo({
          title: data.title || '',
          creatorName: data.creatorName || 'Anonyme',
          creatorProfilePicture: data.creatorProfilePicture,
          creatorCity: data.city || '',
          creatorCountry: data.country || '',
          creatorId: data.userId || ''
        });

        // Récupérer les informations des utilisateurs pour chaque donation
        const donorsWithUserInfo = await Promise.all(
          donations.map(async (donation: any) => {
            if (donation.isAnonymous || !donation.userId) {
              return {
                ...donation,
                date: donation.date?.toDate?.() || donation.date,
                username: 'Anonyme'
              };
            }

            // Récupérer les informations de l'utilisateur
            const userDoc = await getDoc(doc(db, 'users', donation.userId));
            const userData = userDoc.data();
            
            return {
              ...donation,
              date: donation.date?.toDate?.() || donation.date,
              username: userData?.username || 'Utilisateur inconnu'
            };
          })
        );

        // Trier par date décroissante (plus récent en premier)
        const sortedDonors = donorsWithUserInfo.sort((a, b) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );

        setDonors(sortedDonors);
      }
    };
    fetchDonors();
  }, [id]);

  const handleDonorClick = (userId?: string) => {
    if (!userId) return; // Ne rien faire pour les donateurs anonymes
    navigateToPublicProfile(userId, navigate);
  };

  const handleCreatorClick = () => {
    if (requestInfo?.creatorId) {
      navigateToPublicProfile(requestInfo.creatorId, navigate);
    }
  };

  // Filtrer les donateurs en fonction de la recherche
  const filteredDonors = donors.filter(donor => 
    donor.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen">
      <div className="bg-black/50 text-white p-4 flex items-center justify-between sticky top-0 z-10 backdrop-blur-lg border-b border-white/10">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold">Donations</h1>
        <div className="w-6" />
      </div>

      <div className="container mx-auto px-4 py-4 max-w-2xl">
        {/* Informations du créateur et titre de la demande */}
        {requestInfo && (
          <div className="mb-6">
            <div 
              className="flex items-center gap-3 mb-3 cursor-pointer"
              onClick={handleCreatorClick}
            >
              <img
                src={requestInfo.creatorProfilePicture || generateInitialsAvatar(requestInfo.creatorName)}
                alt={requestInfo.creatorName}
                className="w-12 h-12 rounded-full"
              />
              <div>
                <h2 className="text-white font-semibold hover:text-green-400 transition-colors">
                  {requestInfo.creatorName}
                </h2>
                <p className="text-gray-400 text-sm">
                  {requestInfo.creatorCity && requestInfo.creatorCountry 
                    ? `${requestInfo.creatorCity}, ${requestInfo.creatorCountry}`
                    : ''}
                </p>
              </div>
            </div>
            <h3 className="text-white text-lg font-medium border-t border-white/10 pt-3">
              {requestInfo.title}
            </h3>
          </div>
        )}

        {/* Barre de recherche */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Rechercher ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-green-500/50"
          />
        </div>

        {/* Liste des donateurs */}
        {filteredDonors.length > 0 ? (
          filteredDonors.map((donor, index) => (
            <div 
              key={index} 
              className={`bg-white/5 rounded-xl p-2 mb-1 ${
                donor.userId ? 'cursor-pointer hover:bg-white/10' : ''
              }`}
              onClick={() => donor.userId && handleDonorClick(donor.userId)}
            >
              <div className="flex justify-between items-center">
                <span className={`${donor.userId ? 'text-green-400' : 'text-white'}`}>
                  {donor.username}
                </span>
                <span className="text-green-400">{donor.amount} {currency}</span>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-gray-400 mt-4">
            {searchQuery ? "Aucun donateur trouvé" : "Aucun donateur"}
          </div>
        )}
      </div>
    </div>
  );
} 